import { KRATOS_PATH } from '@/lib/auth/kratos-path'

/**
 * Email magic-link sign-in over Ory Kratos' passwordless `code` method (browser flows).
 *
 * 1. `sendSignInEmail` starts a Kratos login flow for the email. An unknown email falls back
 *    to a registration flow, so both cases end at "check your inbox" (no account enumeration).
 * 2. Kratos mails a one-time code plus a link to `/login/magic-link?code=…` (courier templates
 *    in backend/kratos/templates). The flow id stays in this browser's localStorage — Kratos
 *    flows are bound to the CSRF cookie of the browser that started them anyway.
 * 3. `submitSignInCode` (typed code or the link) completes the flow; Kratos then holds an
 *    `ory_kratos_session` cookie, which `signIn('kratos')` exchanges for a Strapi JWT.
 */

const REQUEST_TIMEOUT_MS = 10_000

const PENDING_KEY = 'ctxor:magic-link'
/** kratos.yml `selfservice.flows.{login,registration}.lifespan`. */
const FLOW_LIFESPAN_MS = 10 * 60_000

/** Kratos message ids (https://www.ory.sh/docs/kratos/concepts/ui-messages). */
const MSG_ACCOUNT_NOT_FOUND = 4000035
const MSG_CODE_SENT = new Set([1010014, 1040005])

export type SignInFlowKind = 'login' | 'registration'

export type PendingSignIn = {
  kind: SignInFlowKind
  flowId: string
  email: string
  returnTo?: string
  sentAt: number
}

export type SignInErrorReason = 'expired' | 'invalid_code' | 'unavailable' | 'rejected'

export class SignInCodeError extends Error {
  constructor(readonly reason: SignInErrorReason) {
    super(`Kratos code sign-in failed: ${reason}`)
    this.name = 'SignInCodeError'
  }
}

type KratosMessage = { id: number; type: string; text: string; context?: Record<string, unknown> }
export type KratosFlow = {
  id: string
  state?: string
  return_to?: string
  ui: {
    nodes: { attributes: { name?: string; value?: unknown }; messages?: KratosMessage[] }[]
    messages?: KratosMessage[]
  }
}
type KratosErrorBody = { error?: { id?: string }; redirect_browser_to?: string }

export async function kratosFetch(path: string, body?: object): Promise<Response> {
  try {
    // Relative: Kratos is served from this origin, so its session cookie is first-party.
    return await fetch(`${KRATOS_PATH}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', ...(body && { 'Content-Type': 'application/json' }) },
      body: body && JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    throw new SignInCodeError('unavailable')
  }
}

export const messagesOf = (flow: KratosFlow) => [
  ...(flow.ui.messages ?? []),
  ...flow.ui.nodes.flatMap((node) => node.messages ?? []),
]

export const csrfTokenOf = (flow: KratosFlow) =>
  String(flow.ui.nodes.find((node) => node.attributes.name === 'csrf_token')?.attributes.value ?? '')

/** A Kratos session left over from an earlier sign-in blocks new flows; this app signs out via NextAuth only. */
async function revokeKratosSession() {
  const res = await kratosFetch('/self-service/logout/browser')
  if (!res.ok) return
  const { logout_token: token } = (await res.json()) as { logout_token?: string }
  if (token) await kratosFetch(`/self-service/logout?token=${encodeURIComponent(token)}`)
}

/** `returnTo` is where Kratos sends the browser after a redirect-based method (social sign-in) completes. */
export async function createFlow(kind: SignInFlowKind, returnTo?: string): Promise<KratosFlow> {
  const path = `/self-service/${kind}/browser${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`
  let res = await kratosFetch(path)
  if (res.status === 400) {
    const body = (await res.json()) as KratosErrorBody
    if (body.error?.id !== 'session_already_available') throw new SignInCodeError('rejected')
    await revokeKratosSession()
    res = await kratosFetch(path)
  }
  if (!res.ok) throw new SignInCodeError('unavailable')
  return res.json()
}

export async function fetchFlow(kind: SignInFlowKind, flowId: string): Promise<KratosFlow> {
  const res = await kratosFetch(`/self-service/${kind}/flows?id=${encodeURIComponent(flowId)}`)
  // 403 = CSRF cookie from another browser, 404/410 = unknown or expired flow.
  if ([403, 404, 410].includes(res.status)) throw new SignInCodeError('expired')
  if (!res.ok) throw new SignInCodeError('unavailable')
  return res.json()
}

const identifierFields = (kind: SignInFlowKind, email: string) =>
  kind === 'login' ? { identifier: email } : { traits: { email } }

/** POST a code-method step. Kratos answers 400 + the updated flow for "code sent" and for errors. */
async function submitStep(kind: SignInFlowKind, flow: KratosFlow, fields: object) {
  const res = await kratosFetch(`/self-service/${kind}?flow=${encodeURIComponent(flow.id)}`, {
    method: 'code',
    csrf_token: csrfTokenOf(flow),
    ...fields,
  })
  const body = (await res.json().catch(() => ({}))) as KratosFlow & KratosErrorBody
  return { status: res.status, body }
}

const readPending = (): PendingSignIn | null => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_KEY) ?? 'null') as PendingSignIn | null
    return pending && Date.now() - pending.sentAt < FLOW_LIFESPAN_MS ? pending : null
  } catch {
    return null
  }
}

export const getPendingSignIn = readPending

export const clearPendingSignIn = () => localStorage.removeItem(PENDING_KEY)

/**
 * Email a sign-in code + magic link to `email`, signing up the address if it has no account.
 * `linkFlow` continues the login flow Kratos opened to link a social account to this email
 * (see readSocialRedirect); signing in on that flow attaches the social credential.
 */
export async function sendSignInEmail(
  email: string,
  returnTo?: string,
  linkFlow?: KratosFlow
): Promise<PendingSignIn> {
  for (const kind of linkFlow ? (['login'] as const) : (['login', 'registration'] as const)) {
    const flow = linkFlow ?? (await createFlow(kind))
    const { status, body } = await submitStep(kind, flow, identifierFields(kind, email))
    const messages = status === 400 && body.ui ? messagesOf(body) : []

    if (messages.some((message) => MSG_CODE_SENT.has(message.id)) || body.state === 'sent_email') {
      const pending: PendingSignIn = { kind, flowId: flow.id, email, returnTo, sentAt: Date.now() }
      localStorage.setItem(PENDING_KEY, JSON.stringify(pending))
      return pending
    }
    if (kind === 'login' && messages.some((message) => message.id === MSG_ACCOUNT_NOT_FOUND)) continue
    throw new SignInCodeError(status >= 500 ? 'unavailable' : 'rejected')
  }
  throw new SignInCodeError('rejected')
}

/** Finish the pending flow with the emailed code; resolves once Kratos has set its session cookie. */
export async function submitSignInCode(code: string, pending = readPending()): Promise<PendingSignIn> {
  if (!pending) throw new SignInCodeError('expired')

  const flow = await fetchFlow(pending.kind, pending.flowId)
  const { status, body } = await submitStep(pending.kind, flow, {
    ...identifierFields(pending.kind, pending.email),
    code: code.trim(),
  })

  // 422 = Kratos wants a browser redirect after success (e.g. a continue_with hook).
  if (status === 200 || (status === 422 && body.redirect_browser_to)) {
    clearPendingSignIn()
    return pending
  }
  if (status === 410 || status === 403) throw new SignInCodeError('expired')
  if (status === 400) throw new SignInCodeError('invalid_code')
  throw new SignInCodeError('unavailable')
}
